import { CircularProgress, Typography, useTheme } from '@material-ui/core'
import {
  CheckCircleOutlined,
  ErrorOutlined,
  QuestionAnswerOutlined,
  TimerOutlined,
} from '@material-ui/icons'
import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@material-ui/lab'
import { BomItemWorkingStepSummary } from 'features/BillOfMaterials/components/BomItemWorkingStepSummary'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { WorkingStepStats } from 'features/BillOfMaterials/store/selectors/assemblyWorkingStepsSummaryWithCounterSelector'
import { isEqual } from 'lodash'
import React, { CSSProperties, useCallback, useEffect } from 'react'
import { useAppSelector } from 'store/configureStore'
import {
  ProjectOperationDto,
  ProjectOperationStatus,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { OperationGroupChildrensBoxes } from './GeometryLogChildrenBoxes'
import { useProjectOperationsStyles } from './ProjectOperationsLogsDrawer.styles'

const OperationIcon = (props: {
  operationGroup: ProjectOperationDto
  checkChildrens?: boolean
}) => {
  const theme = useTheme()

  switch (props.operationGroup.status) {
    case ProjectOperationStatus.Failed: {
      return <ErrorOutlined color="secondary" />
    }
    case ProjectOperationStatus.Started: {
      return <CircularProgress size="24px" disableShrink />
    }
    case ProjectOperationStatus.Queued: {
      return <TimerOutlined color="action" />
    }
    case ProjectOperationStatus.Finished: {
      return (
        <CheckCircleOutlined style={{ color: theme.palette.success.main }} />
      )
    }
    default:
      return <QuestionAnswerOutlined />
  }
}

const GeometryLogItem = ({
  geometryLog: operationGroup,
  assemblyIds,
}: {
  geometryLog: ProjectOperationDto
  assemblyIds: string[]
  style?: CSSProperties
}) => {
  const classes = useProjectOperationsStyles()

  const [primaryWorkingStepsFilter, setPrimaryWorkingStepsFilter] =
    React.useState<WorkingStepStats[] | null>(null)

  const bomItemIdForWorkingStepSummary =
    operationGroup.children.length === 1
      ? operationGroup.children[0].boMItemId
      : operationGroup.children?.find((x) => assemblyIds?.includes(x.boMItemId))
          ?.boMItemId

  const workingStepsStats = useAppSelector(
    projectSelectors.assemblyWorkingStepsSummaryWithCounterSelector(
      bomItemIdForWorkingStepSummary
    ),
    isEqual
  )

  useEffect(() => {
    primaryWorkingStepsFilter?.forEach((filter) => {
      if (!workingStepsStats[filter.primaryWorkingStep]) {
        setPrimaryWorkingStepsFilter((prevState) =>
          prevState.filter(
            (x) => x.primaryWorkingStep !== filter.primaryWorkingStep
          )
        )
      }
    })
  }, [primaryWorkingStepsFilter, workingStepsStats])

  const handleWorkingStepFilterChange = useCallback(
    (workingStepStats: WorkingStepStats) => {
      setPrimaryWorkingStepsFilter((current) => {
        let ret = []

        if (!current) {
          ret = [workingStepStats]
          return ret
        }
        if (
          current.findIndex(
            (x) => x.primaryWorkingStep === workingStepStats.primaryWorkingStep
          ) === -1
        ) {
          ret = [...current, workingStepStats]
        } else {
          ret = current?.filter(
            (x) => x.primaryWorkingStep !== workingStepStats.primaryWorkingStep
          )
        }

        if (ret.length > 0) {
          return ret
        } else {
          return null
        }
      })
    },
    []
  )

  const getButtonVariant = useCallback(
    (workingStep: WorkingStepType): 'filled' | 'outlined' => {
      return primaryWorkingStepsFilter?.findIndex(
        (x) => x.primaryWorkingStep === workingStep
      ) > -1
        ? 'filled'
        : 'outlined'
    },
    [primaryWorkingStepsFilter]
  )

  if (!operationGroup) return null

  return (
    <TimelineItem key={operationGroup.id}>
      <TimelineOppositeContent>
        {operationGroup?.children ? (
          <BomItemWorkingStepSummary
            bomItemId={bomItemIdForWorkingStepSummary}
            includeCountOfWorkingSteps
            includeAssemblyWorkingSteps
            onClick={handleWorkingStepFilterChange}
            getButtonVariant={getButtonVariant}
            // shouldFilter
          />
        ) : null}
      </TimelineOppositeContent>
      <TimelineSeparator>
        <TimelineDot
          variant="outlined"
          // color="primary"
          className={classes.timelineDot}
        >
          <OperationIcon checkChildrens operationGroup={operationGroup} />
        </TimelineDot>
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineContent>
        <Typography variant="body2">
          {operationGroup.operationTypeTranslationArguments}
        </Typography>
        {operationGroup.totalChildrenCount > 0 ? (
          <Typography variant="caption" component="p" color="textSecondary">
            {operationGroup.totalChildrenFinishedCount} /{' '}
            {operationGroup.totalChildrenCount}
          </Typography>
        ) : null}

        {operationGroup.errorMessage ? (
          <Typography variant="caption" component="p" color="secondary">
            {operationGroup.errorMessage}
          </Typography>
        ) : null}

        <OperationGroupChildrensBoxes
          operationGroup={operationGroup}
          primaryWorkingStepsFilter={primaryWorkingStepsFilter}
        />
      </TimelineContent>
    </TimelineItem>
  )
}

export default React.memo(GeometryLogItem, isEqual)
